import { Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDepartmentFacility } from "../../../../hooks/useDepartmentFacility";
import { departmentNameInFacility } from "../../../../utils/facilityDepartments";
import type { FacilityAnnouncementAudience } from "../../../../utils/models";
import type { DropdownItem } from "../../../UIToolkit/Inputs/SystemDropDown";
import { useFacilityAnnouncementAudience } from "../hooks";

/** The composer's role, job and employment-type choice, as an announcement sends it: null where it doesn't narrow. */
export type AnnouncementFilters = Pick<FacilityAnnouncementAudience, "roles" | "jobIds" | "employmentTypes">;

/** The Department dropdown's "All departments (entire facility)" row. Not a department id: those look like `Network__Name`. */
export const ENTIRE_FACILITY_OPTION = "entire-facility";

/** What the Department dropdown has picked: the entire facility, or departments of it. */
export type DepartmentChoice = { entireFacility: boolean; departmentIds: string[] };

/**
 * The next choice once the dropdown reports `picked`. "All departments (entire facility)" is its own choice, not
 * every department ticked: picking it clears the departments, and picking a department clears it. That keeps it
 * meaningful in a one-department building, where ticking the only department is just "this department".
 */
export const nextDepartmentChoice = (previous: DepartmentChoice, picked: string[]): DepartmentChoice => {
	const departmentIds = picked.filter((value) => value !== ENTIRE_FACILITY_OPTION);
	if (picked.includes(ENTIRE_FACILITY_OPTION) && !previous.entireFacility) return { entireFacility: true, departmentIds: [] };
	if (previous.entireFacility && departmentIds.length > 0) return { entireFacility: false, departmentIds };
	return { entireFacility: picked.includes(ENTIRE_FACILITY_OPTION), departmentIds };
};

const RecipientCount = ({ count }: { count: number }) => (
	<Typography variant="caption" sx={{ color: "text.secondary", marginInlineStart: "auto" }}>
		{count}
	</Typography>
);

/** A count once the server has one, for a dropdown row. */
const countAdornment = (count: number | undefined) => (count === undefined ? undefined : <RecipientCount count={count} />);

/**
 * The composer's Department choice. It starts on this department alone, which is the composer as it always was —
 * staff picked here, by login. Anything else makes the message an announcement to the building: the server texts
 * everyone the choice reaches. "All departments (entire facility)" is everyone in the building, staff in no
 * department included, sent as `departmentIds: null`; departments are just their staff. The counts are texts — one
 * per phone number — under the role, job and employment-type choice. If the building's departments can't be loaded,
 * only this department can be messaged.
 */
export const useFacilityAnnouncementScope = (allowFacilityScope: boolean | undefined, filters: AnnouncementFilters) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const currentDepartmentId = chosenDepartment.department_id;
	const [choice, setChoice] = useState<DepartmentChoice>({ entireFacility: false, departmentIds: [currentDepartmentId] });

	const facilityQuery = useDepartmentFacility({ departmentId: currentDepartmentId, enabled: Boolean(allowFacilityScope) });
	const building = facilityQuery.data;
	const onlyThisDepartment = !choice.entireFacility && choice.departmentIds.length === 1 && choice.departmentIds[0] === currentDepartmentId;
	const isFacilityAnnouncement = Boolean(allowFacilityScope) && !onlyThisDepartment;
	const nothingPicked = !choice.entireFacility && choice.departmentIds.length === 0;
	// A filter with nothing ticked matches nobody, so there is nothing to count or send.
	const filtersMatchNobody = [filters.roles, filters.jobIds, filters.employmentTypes].some((list) => list !== null && list.length === 0);

	const audience = useMemo<FacilityAnnouncementAudience>(
		() => ({ departmentIds: choice.entireFacility ? null : choice.departmentIds, ...filters }),
		[choice, filters],
	);
	const countsQuery = useFacilityAnnouncementAudience(currentDepartmentId, audience, building !== undefined && !nothingPicked && !filtersMatchNobody);
	const counts = countsQuery.data;

	const departmentOptions = useMemo<DropdownItem<string>[] | undefined>(() => {
		if (!building) return undefined;
		const entireFacility = {
			value: ENTIRE_FACILITY_OPTION,
			id: ENTIRE_FACILITY_OPTION,
			label: t("broadcast.departments.all"),
			endAdornment: countAdornment(counts?.facilityRecipients),
		};
		const departments = building.departments.map((department) => {
			const name = departmentNameInFacility(department.display_name, building.name, t("header.facilitySwitcher.mainDepartment"));
			return {
				value: department.department_id,
				id: department.department_id,
				label: department.department_id === currentDepartmentId ? t("broadcast.departments.this", { department: name }) : name,
				endAdornment: countAdornment(counts?.departmentRecipients[department.department_id]),
			};
		});
		return [entireFacility, ...departments];
	}, [building, counts, currentDepartmentId, t]);

	const onDepartmentsPicked = useCallback((picked: string[]) => setChoice((previous) => nextDepartmentChoice(previous, picked)), []);

	// undefined: not known yet — loading (the last answer, kept on screen for the department rows, was for another
	// choice), or the count failed; the send still works.
	const recipientCount = nothingPicked || filtersMatchNobody ? 0 : countsQuery.isPlaceholderData ? undefined : counts?.recipients;

	return {
		isFacilityAnnouncement,
		facilityName: building?.name,
		departmentOptions,
		pickedOptionIds: choice.entireFacility ? [ENTIRE_FACILITY_OPTION] : choice.departmentIds,
		onDepartmentsPicked,
		departmentsUnavailable: Boolean(allowFacilityScope) && facilityQuery.isError,
		audience,
		recipientCount,
		// The count failed (not still loading): the composer says so instead of counting forever.
		recipientCountFailed: recipientCount === undefined && countsQuery.isError,
		hasRecipients: recipientCount !== 0,
	};
};
